/**
 * Image Service - Free image API integration
 *
 * Uses Picsum Photos for placeholder/educational images.
 * (The Unsplash Source API used for abstract backgrounds was discontinued and
 * has been removed — landing sections now use CSS gradients only.)
 */

export interface ImageOptions {
  width?: number;
  height?: number;
  keywords?: string;
}

/**
 * Get a placeholder image URL using Picsum Photos
 * Uses seeds to get consistent images that look educational
 * @param seed - Seed number for consistent image (different seeds = different images)
 * @param width - Image width in pixels
 * @param height - Image height in pixels
 * @returns Picsum image URL
 */
export const getPlaceholderImage = (
  seed: number,
  width: number = 800,
  height: number = 600
): string => {
  return `https://picsum.photos/seed/${seed}/${width}/${height}`;
};

/**
 * Predefined image URLs for common educational themes
 * Using Picsum Photos with seeds for consistent educational-looking images
 */
export const educationalImages = {
  hero: getPlaceholderImage(101, 1200, 800),
  students: getPlaceholderImage(102, 800, 600),
  classroom: getPlaceholderImage(103, 800, 600),
  community: getPlaceholderImage(104, 800, 600),
  teacher: getPlaceholderImage(105, 800, 600),
  activities: getPlaceholderImage(106, 800, 600),
  bible: getPlaceholderImage(107, 800, 600),
  homeschool: getPlaceholderImage(108, 800, 600),
  preschool: getPlaceholderImage(201, 800, 600),
  elementary: getPlaceholderImage(202, 800, 600),
  middleSchool: getPlaceholderImage(203, 800, 600),
  highSchool: getPlaceholderImage(204, 800, 600),
  math: getPlaceholderImage(301, 800, 600),
  science: getPlaceholderImage(302, 800, 600),
  art: getPlaceholderImage(303, 800, 600),
  music: getPlaceholderImage(304, 800, 600),
  history: getPlaceholderImage(305, 800, 600),
  language: getPlaceholderImage(306, 800, 600),
  pe: getPlaceholderImage(307, 800, 600),
} as const;
